/**
 * CancelBookingPage - Page for canceling a scheduled booking.
 * Accessed via /booking/:cancelToken/cancel
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { cancelBooking, getRescheduleDetails } from '@/lib/api/scheduling';
import type {
  BookingSummary,
  BookingTypeSummary,
} from '@/lib/api/types/scheduling';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { logger } from '@/lib/logger';

import { BookingLayout } from './BookingLayout';

type CancelState = 'loading' | 'confirm' | 'canceling' | 'success' | 'error';

interface BookingDetails {
  booking: BookingSummary;
  meetingType: BookingTypeSummary;
}

/**
 * Format a date/time for display.
 */
function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CancelBookingPage() {
  const { cancelToken } = useParams<{ cancelToken: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<CancelState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(
    null
  );
  const [reason, setReason] = useState('');

  // Fetch booking details on mount
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!cancelToken) {
        setError('Invalid cancellation link');
        setState('error');
        return;
      }

      try {
        // Use getRescheduleDetails to fetch booking info by cancel token
        const response = await getRescheduleDetails(cancelToken);
        setBookingDetails({
          booking: response.booking,
          meetingType: response.booking_type,
        });
        setState('confirm');
      } catch (err) {
        logger.error(
          'Error fetching booking details for cancellation',
          err instanceof Error ? err : new Error(String(err))
        );
        setError(
          "We couldn't load your booking details. The link may be invalid or expired."
        );
        setState('error');
      }
    };

    fetchBookingDetails();
  }, [cancelToken]);

  const handleCancel = async () => {
    if (!cancelToken) {
      setError('Invalid cancellation link');
      setState('error');
      return;
    }

    setState('canceling');

    try {
      await cancelBooking({
        cancel_token: cancelToken,
        reason: reason.trim() || undefined,
      });
      setState('success');
    } catch (err) {
      logger.error(
        'Error canceling booking',
        err instanceof Error ? err : new Error(String(err))
      );
      setError("We couldn't cancel this booking. Try again.");
      setState('error');
    }
  };

  const handleGoBack = useCallback(() => navigate(-1), [navigate]);
  const handleTryAgain = useCallback(() => setState('confirm'), []);

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <div className='flex items-center justify-center py-16'>
            <LoadingIndicator size='md' label='Loading booking details…' />
          </div>
        );

      case 'confirm':
        return (
          <div className='py-8'>
            {/* Warning icon */}
            <div className='text-center mb-6'>
              <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4'>
                <svg
                  className='w-8 h-8 text-amber-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                  />
                </svg>
              </div>
              <h1 className='text-xl font-semibold text-fg-primary mb-2'>
                Cancel Booking
              </h1>
              <p className='text-fg-secondary max-w-md mx-auto'>
                Are you sure you want to cancel this booking? This action cannot
                be undone.
              </p>
            </div>

            {/* Booking details card */}
            {bookingDetails && (
              <div className='bg-bg-secondary border border-border-secondary rounded-lg p-6 mb-6 max-w-md mx-auto'>
                <h2 className='font-medium text-fg-primary mb-4'>
                  Booking Details
                </h2>
                <dl className='space-y-3 text-sm'>
                  <div>
                    <dt className='text-fg-tertiary'>Meeting</dt>
                    <dd className='text-fg-primary font-medium'>
                      {bookingDetails.meetingType.name}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-fg-tertiary'>When</dt>
                    <dd className='text-fg-primary'>
                      {formatDateTime(bookingDetails.booking.start_time)}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-fg-tertiary'>Duration</dt>
                    <dd className='text-fg-primary'>
                      {bookingDetails.meetingType.duration_minutes} minutes
                    </dd>
                  </div>
                  <div>
                    <dt className='text-fg-tertiary'>Booked by</dt>
                    <dd className='text-fg-primary'>
                      {bookingDetails.booking.booker_name} (
                      {bookingDetails.booking.booker_email})
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            {/* Cancellation reason (optional) */}
            <div className='max-w-md mx-auto mb-6'>
              <label
                htmlFor='cancel-reason'
                className='block text-sm font-medium text-fg-secondary mb-2'
              >
                Reason for cancellation (optional)
              </label>
              <textarea
                id='cancel-reason'
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Let them know why you're cancelling"
                className='w-full px-3 py-2 border border-border-secondary rounded-lg bg-bg-primary text-fg-primary placeholder-fg-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none'
                rows={3}
              />
            </div>

            {/* Action buttons */}
            <div className='flex justify-center gap-4'>
              <Button color='secondary' size='md' onClick={handleGoBack}>
                Go Back
              </Button>
              <Button
                color='primary-destructive'
                size='md'
                onClick={handleCancel}
              >
                Cancel Booking
              </Button>
            </div>
          </div>
        );

      case 'canceling':
        return (
          <div className='flex items-center justify-center py-16'>
            <LoadingIndicator size='md' label='Cancelling booking…' />
          </div>
        );

      case 'success':
        return (
          <div className='text-center py-12'>
            <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4'>
              <svg
                className='w-8 h-8 text-green-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h1 className='text-xl font-semibold text-fg-primary mb-2'>
              Booking Cancelled
            </h1>
            <p className='text-fg-secondary'>
              Your booking has been successfully cancelled. A confirmation email
              has been sent.
            </p>
          </div>
        );

      case 'error':
        return (
          <div className='text-center py-12'>
            <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4'>
              <svg
                className='w-8 h-8 text-red-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </div>
            <h1 className='text-xl font-semibold text-fg-primary mb-2'>
              Unable to Cancel
            </h1>
            <p className='text-fg-secondary max-w-md mx-auto'>
              {error || 'An error occurred. Please try again later.'}
            </p>
            {bookingDetails && (
              <Button
                color='primary'
                size='md'
                className='mt-6'
                onClick={handleTryAgain}
              >
                Try Again
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return <BookingLayout>{renderContent()}</BookingLayout>;
}
